/* @flow */

import Typenames from './Typenames'
import { ActionTypes, store } from 'store'
import _ from 'lodash'

/**
 * Restriction represents a set of flow/api restrictions in play for a given primary group and version.
 * @typedef { Object } Restriction
 * @memberof RestrictionService
 */
export type Restriction = {
  id?: string,
  version?: string,
  forVersion: string,
  forGroup: string,
  restrictions: Array<ModuleRestriction>
  // e.g. [
  //   { type: "Http", name: "upload",
  //     actions: [
  //       { kind: "PARAM",
  //         params: {
  //           url: { "match": "dr.dk", "action": "LOG" }
  //         }
  //       }
  //     ]
  //   }
}

export type ModuleRestriction = {
  module: String,
  method: String,
  action: String,
  kindParams?: Array<Object>,
  actionParams?: Array<Object>,
  actions: Array<String>
}

const defaultRestriction = Object.freeze({
  forVersion: '',
  forGroup: '',
  restrictions: []
})

/**
 * Restrictions typename
 * @private
 * @memberof RestrictionService
 */
const TYPENAME = Typenames.RESTRICTIONS

const uniqueConstraints = []

/**
 * Creates a new restriction.
 * @function
 * @memberof RestrictionService
 *
 * @return { Promise<string> } The id of the added restriction.
 */
const create = (r: Restriction): Promise<PostResponse> => {
  return store.dispatch(ActionTypes.CREATE, {
    typename: TYPENAME,
    item: r,
    uniqueConstraints: uniqueConstraints
  })
}

/**
 * Reads a restriction, flattening 'descriptive'.
 * @function
 * @memberof RestrictionService
 *
 * @return { Promise<Restriction> } The restriction object.
 */
const read = (id: string): Promise<Object> => {
  return store.dispatch(ActionTypes.GET_BY_ID, { typename: TYPENAME, id: id }).then(restriction => {
    return { ...defaultRestriction, ...restriction }
  })
}

/**
 * Updates a restriction.
 * @function
 * @memberof RestrictionService
 *
 * @return { Promise<any> }
 */
const update = (id: string, r: Restriction): Promise<PostResponse> => {
  return store.dispatch(ActionTypes.UPDATE, {
    typename: TYPENAME,
    id: id,
    item: r,
    uniqueConstraints: uniqueConstraints
  })
}

/**
 * Deletes a restriction.
 * @function
 * @memberof RestrictionService
 *
 * @return { Promise<any> }
 */
const remove = (id: string): Promise<Object> => {
  return store.dispatch(ActionTypes.DELETE, { typename: TYPENAME, id: id })
}

/**
 * Reads all restrictions flattening descriptive.
 * @function
 * @memberof RestrictionService
 *
 * @return { Observable<Array<Restriction>> } All the restrictions.
 */
const readAll = (): Promise<Object> => {
  return store.dispatch(ActionTypes.GET, TYPENAME).then(restrictions => {
    return _.map(restrictions, r => {
      return {
        ...defaultRestriction,
        ...r
      }
    })
  })
}

/**
 * Copies a restriction.
 * @function
 * @memberof RestrictionService
 *
 * @return { Promise<string> }
 */
const copy = (id: string, nameSuffix: string): Promise<PostResponse> => {
  return self.read(id).then(r => {
    const toCopy = { ...r }
    delete toCopy.id
    delete toCopy.forVersion
    delete toCopy.forGroup

    return self.create(toCopy)
  })
}

const self = {
  create,
  read,
  update,
  remove,
  copy,
  readAll
}

/**
 * Restriction service: CRUD interface for restrictions.
 * @module RestrictionService
 * @namespace RestrictionService
 * @version 1.0.0
 */
export default self
